import { FormGroup } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { addRemoveError, getControl, getFormArray } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { REQUIRED_STATUS } from '@shared/constants/shared.constants';
import { CkNormeValues } from '../common/models/amm-controllo.type';
import { CkNormeValuesEnum } from '../common/consts/ckNormeValues.enum';
import { messaggeCodFisc, regexCodFisc } from '../../../../features/ammin-controllo/ammin-controllo.constants';
import { toNum } from '@shared/helpers/math.helper';
import { formArrayRequiredHelper } from '../common/helpers/norme-validators.helper';
import { futureOrInconsistentDate, ruleGuard } from './amm-controllo-validators.helpers';
import { formPaths } from '@shared/constants/form-paths.const';

const AMM_CTRL_AMMUNICO_PATH = `${formPaths.ammControllo}.my_AllAmminUnico`;

const my_AllAmmUnicoRequiredHelper = (form: FormGroup, targetPath: string) => {
    const my_AllAmminUnicoFormArray = getFormArray(form, AMM_CTRL_AMMUNICO_PATH);
    formArrayRequiredHelper(my_AllAmminUnicoFormArray, targetPath);
};

export const ammUnicoRules = (ckNormeTargetValue: CkNormeValues) => ({
    my_AllAmmUnDataElezione: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const my_AllAmminUnicoFormArray = getFormArray(form, AMM_CTRL_AMMUNICO_PATH);

            my_AllAmminUnicoFormArray.controls.map((group: FormGroup) => {
                const my_AllAmmUnicoDataElezioneControl = getControl(group, 'my_AllAmmUnDataElezione');
                const errors = my_AllAmmUnicoDataElezioneControl.errors ? my_AllAmmUnicoDataElezioneControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllAmmUnicoDataElezioneControl?.value), errors, 'my_AllAmmUnDataElezione', {
                    status: REQUIRED_STATUS,
                });

                my_AllAmmUnicoDataElezioneControl.setErrors(isEqual(errors, {}) ? null : errors);
                futureOrInconsistentDate(form, my_AllAmmUnicoDataElezioneControl, 'my_AllAmmUnDataElezione', ckNormeTargetValue);
            });
        });
    },

    my_SRLAmmUnicoSocio: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const my_AllAmminUnicoFormArray = getFormArray(form, AMM_CTRL_AMMUNICO_PATH);

            my_AllAmminUnicoFormArray.controls.map((group: FormGroup) => {
                const my_SRLAmmUnicoSocioControl = getControl(group, 'my_SRLAmmUnicoSocio');
                const errors = my_SRLAmmUnicoSocioControl.errors ? my_SRLAmmUnicoSocioControl.errors : {};

                addRemoveError(isNilOrEmpty(my_SRLAmmUnicoSocioControl?.value) || toNum(my_SRLAmmUnicoSocioControl?.value) === 0, errors, 'my_SRLAmmUnicoSocio', {
                    status: REQUIRED_STATUS,
                });

                my_SRLAmmUnicoSocioControl.setErrors(isEqual(errors, {}) ? null : errors);
            });
        });
    },

    my_AllAmmUnCarica: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnCarica');
        });
    },

    my_AllAmmUnCognome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnCognome');
        });
    },

    my_AllAmmUnNome: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnNome');
        });
    },

    my_AllAmmUnVia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnVia');
        });
    },

    my_AllAmmUnProvincia: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnProvincia');
        });
    },

    my_AllAmmUnComune: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnComune');
        });
    },

    my_AllAmmUnCap: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            my_AllAmmUnicoRequiredHelper(form, 'my_AllAmmUnCap');
        });
    },

    my_AllAmmUnCodFisc: (form: FormGroup) => {
        ruleGuard(form, ckNormeTargetValue, [CkNormeValuesEnum.normeSrl, CkNormeValuesEnum.normeSpaOrdinario], () => {
            const my_AllAmminUnicoFormArray = getFormArray(form, AMM_CTRL_AMMUNICO_PATH);

            my_AllAmminUnicoFormArray.controls.map((group: FormGroup) => {
                const my_AllAmmUnCodFiscControl = getControl(group, 'my_AllAmmUnCodFisc');
                const errors = my_AllAmmUnCodFiscControl.errors ? my_AllAmmUnCodFiscControl.errors : {};

                addRemoveError(isNilOrEmpty(my_AllAmmUnCodFiscControl?.value), errors, 'my_AllAmmUnCodFisc', {
                    status: REQUIRED_STATUS,
                });

                addRemoveError(!regexCodFisc.test(my_AllAmmUnCodFiscControl?.value), errors, 'my_AllAmmUnCodFisc', {
                    status: messaggeCodFisc,
                });

                my_AllAmmUnCodFiscControl.setErrors(isEqual(errors, {}) ? null : errors);
            });
        });
    },
});
